import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js'

// Stripe appearance configuration that matches your design system
const defaultAppearance = {
  theme: 'stripe' as const,
  variables: {
    colorPrimary: '#10b981', // Primary green color from your design system
    colorBackground: '#ffffff',
    colorText: '#374151',
    colorDanger: '#ef4444',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    spacingUnit: '4px',
    borderRadius: '6px',
  }
}

// Cache for Stripe promises keyed by publishable key
const stripePromises = new Map<string, Promise<Stripe | null>>()

/**
 * Get or create a Stripe promise for the given publishable key
 * Uses runtime configuration passed from server loader data
 * 
 * Usage:
 * ```tsx
 * const { stripePublishableKey } = Route.useLoaderData()
 * const stripePromise = getStripePromise(stripePublishableKey)
 * ```
 * 
 * @param publishableKey - The Stripe publishable key from runtime config
 * @returns Promise that resolves to the Stripe instance
 */
export const getStripePromise = (publishableKey: string): Promise<Stripe | null> => {
  if (!publishableKey) {
    throw new Error('Stripe publishable key is required. Make sure to pass it from your route loader data.')
  }
  
  if (!stripePromises.has(publishableKey)) {
    stripePromises.set(publishableKey, loadStripe(publishableKey))
  }
  
  return stripePromises.get(publishableKey)!
}

// Default Elements options that match your design system
export const elementsOptions: StripeElementsOptions = {
  appearance: defaultAppearance,
  locale: 'en'
}
